/**
 * Service d'Intelligence Artificielle pour ADHD Planner
 * Utilise l'API Google Gemini pour le parsing et les suggestions
 */

import { captureError, SEVERITY } from './errorService';

const API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-lite:generateContent';
const STORAGE_KEY = 'adhd-planner-ai-cache';
const USAGE_KEY = 'adhd-planner-ai-usage';

// Limites de coûts (Gemini est gratuit jusqu'à 15 RPM, 1M tokens/jour)
const DAILY_TOKEN_LIMIT = 500000;
const MAX_REQUESTS_PER_MINUTE = 15;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 heures

// Prompts système
const PROMPTS = {
  parseTask: `Tu es un assistant pour une application de planning ADHD en français.
Analyse ce texte et extrait les informations de tâche.

Texte: "{input}"
Date actuelle: {currentDate}
Heure actuelle: {currentTime}

Réponds UNIQUEMENT en JSON valide (pas de markdown, pas de backticks, pas de texte avant ou après):
{
  "task": "nom de la tâche (court, impératif)",
  "time": "HH:MM (24h) ou null si non spécifié",
  "duration": nombre en minutes (défaut 30),
  "category": "vital" ou "chore" ou "pleasure" ou "reward",
  "date": "YYYY-MM-DD",
  "instructions": ["sous-tâche 1", "sous-tâche 2"] ou [],
  "confidence": 0 à 1
}

Règles:
- "demain" = date + 1 jour
- "après-demain" = date + 2 jours
- "matin" = entre 08:00 et 12:00 (suggère 09:00)
- "après-midi" = entre 14:00 et 18:00 (suggère 14:00)
- "soir" = entre 19:00 et 21:00 (suggère 19:00)
- Mots-clés vitaux: médicament, pilule, docteur, médecin, traitement, rdv santé
- Mots-clés corvée: ménage, courses, travail, réunion, appel, email, lessive
- Mots-clés plaisir: pause, café, ami, jeu, film, repos, marche, lecture
- Mots-clés récompense: cadeau, achat, shopping, resto
- Si durée non spécifiée, utilise 30 min
- Génère des sous-tâches si la tâche est complexe`,

  suggestions: `Tu es un coach ADHD bienveillant et encourageant en français.
Génère 3-5 suggestions personnalisées basées sur le contexte.

Contexte actuel:
- Heure: {time}
- Jour: {dayName}
- Humeur: {mood}/5 ({moodLabel})
- Énergie: {energy}/4 ({energyLabel})
- Tâches complétées aujourd'hui: {completedCount}
- Tâches vitales restantes: {remainingVital}
- Pattern détecté: {pattern}

Réponds UNIQUEMENT en JSON valide (pas de markdown):
{
  "suggestions": [
    {
      "text": "message court et bienveillant",
      "type": "reminder" ou "encouragement" ou "task" ou "break",
      "task": { "task": "nom", "time": "HH:MM", "duration": 30, "category": "chore" } ou null,
      "priority": 1 à 5,
      "emoji": "emoji approprié"
    }
  ]
}

Règles ADHD importantes:
- Si énergie <= 2: suggérer des tâches faciles ou des pauses, pas de pression
- Si humeur <= 2: messages doux et encourageants, célébrer les petites victoires
- Matin (6-12h): tâches importantes, médicaments, routine
- Après-midi (12-17h): rappeler les pauses régulières
- Soir (18-22h): routine de fin de journée, pas de nouvelles tâches complexes
- Toujours inclure au moins un encouragement
- Si beaucoup de tâches complétées: féliciter !
- Si peu de tâches complétées: pas de jugement, encourager`,

  generateSubTasks: `Tu es un assistant spécialisé TDAH/bipolaire en français.
Tu aides une personne habitant au 46 bis Avenue Jean Jaurès, 75019 Paris.
Elle a besoin d'être complètement guidée étape par étape avec des suggestions CONCRÈTES et UTILES.

Tâche à décomposer: "{taskName}"
Catégorie: {category}
Durée prévue: {duration} minutes
Contexte: {context}

Génère des sous-tâches TRÈS détaillées avec des INITIATIVES CONCRÈTES:

Pour la CUISINE/repas:
- Suggère une recette SIMPLE adaptée TDAH (peu d'étapes, ingrédients basiques)
- Inclus un lien direct vers la recette (marmiton.org, cuisineaz.com, etc.)
- Propose des alternatives si pas tous les ingrédients

Pour les SORTIES/balades:
- Suggère des lieux PROCHES du 19ème arrondissement de Paris:
  * Parc des Buttes-Chaumont (5 min à pied)
  * Canal de l'Ourcq (10 min)
  * Parc de la Villette (15 min)
  * Bassin de la Villette (10 min)
- Donne l'adresse exacte et le temps de marche

Pour le MÉNAGE:
- Divise en micro-tâches de 5 min max
- Suggère la méthode "une pièce à la fois"
- Propose de mettre de la musique motivante

Pour les COURSES:
- Suggère les magasins proches (Carrefour City, Franprix du quartier)
- Propose une liste simplifiée

Pour les MÉDICAMENTS:
- Rappelle l'importance, sans culpabiliser
- Suggère de mettre une alarme récurrente

Réponds UNIQUEMENT en JSON valide:
{
  "subTasks": [
    {
      "step": 1,
      "title": "🎯 titre avec emoji",
      "description": "instruction détaillée et bienveillante",
      "duration": minutes (2-10 max),
      "tip": "💡 conseil pratique ou encouragement",
      "isCheckpoint": true/false,
      "suggestion": {
        "type": "recipe" ou "place" ou "music" ou "tip" ou null,
        "title": "Titre de la suggestion",
        "url": "lien direct si applicable",
        "details": "détails supplémentaires (adresse, temps, etc.)"
      }
    }
  ],
  "totalDuration": durée totale estimée,
  "difficultyLevel": "facile" ou "moyen" ou "difficile",
  "encouragement": "message motivant personnalisé avec emoji",
  "breakReminder": "rappel de pause si tâche > 30min",
  "mainSuggestion": {
    "type": "recipe/place/music/shopping",
    "title": "Suggestion principale pour cette tâche",
    "url": "lien si applicable",
    "details": "description courte"
  }
}

Règles importantes:
- TOUJOURS inclure des emojis dans les titres
- TOUJOURS ajouter un tip/commentaire encourageant à chaque étape
- Proposer des suggestions CONCRÈTES et LOCALES quand pertinent
- Première étape ultra-facile pour vaincre l'inertie
- Maximum 5 minutes par sous-tâche
- Langage positif, jamais culpabilisant
- Pour les checkpoints: célébrer les petites victoires`,

  planDay: `Tu es un planificateur ADHD-friendly en français.
Organise ces tâches dans la journée de manière optimale.

Tâches à placer:
{tasksJSON}

Contraintes:
- Créneaux disponibles: {availableSlots}
- Niveau énergie: {energyLevel}/4
- Préférence: {preference}

Réponds UNIQUEMENT en JSON valide:
{
  "schedule": [
    {
      "taskId": "id de la tâche",
      "suggestedTime": "HH:MM",
      "reason": "explication courte"
    }
  ],
  "warnings": ["avertissement si surcharge ou conflit"]
}

Règles ADHD:
- Maximum 2 tâches corvée consécutives
- Pause 15min après toute tâche > 1h
- Tâches vitales (médicaments) le matin
- Alterner les catégories (corvée → plaisir → corvée)
- Laisser du temps buffer entre les tâches (15 min)
- Ne pas surcharger: max 6-8 tâches/jour`
};

class AIService {
  constructor() {
    this.apiKey = null;
    this.isInitialized = false;
    this.cache = new Map();
    this.usageStats = {
      tokensUsed: 0,
      requestsToday: 0,
      lastReset: null
    };
    this.requestTimestamps = [];
  }

  /**
   * Initialiser le service
   */
  async init() {
    // Utiliser la clé Google API existante
    this.apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

    if (!this.apiKey) {
      console.warn('⚠️ REACT_APP_GOOGLE_API_KEY non configurée');
      return false;
    }

    // Charger le cache et les stats
    this.loadCache();
    this.loadUsageStats();

    this.isInitialized = true;
    console.log('✅ AI Service (Gemini) initialisé');
    return true;
  }

  /**
   * Vérifier si le service est disponible
   */
  isAvailable() {
    return this.isInitialized && !!this.apiKey;
  }

  /**
   * Charger le cache depuis localStorage
   */
  loadCache() {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        // Filtrer les entrées expirées
        const now = Date.now();
        Object.entries(data).forEach(([key, value]) => {
          if (value.expiresAt > now) {
            this.cache.set(key, value);
          }
        });
      }
    } catch (e) {
      console.warn('Erreur chargement cache IA:', e);
    }
  }

  /**
   * Sauvegarder le cache dans localStorage
   */
  saveCache() {
    try {
      const data = {};
      this.cache.forEach((value, key) => {
        data[key] = value;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Erreur sauvegarde cache IA:', e);
    }
  }

  /**
   * Obtenir depuis le cache
   */
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }
    return null;
  }

  /**
   * Mettre en cache
   */
  setCache(key, data, ttl = CACHE_TTL) {
    this.cache.set(key, {
      data,
      expiresAt: Date.now() + ttl
    });
    this.saveCache();
  }

  /**
   * Charger les stats d'utilisation
   */
  loadUsageStats() {
    try {
      const stats = localStorage.getItem(USAGE_KEY);
      if (stats) {
        this.usageStats = JSON.parse(stats);

        // Réinitialiser si nouveau jour
        const today = new Date().toISOString().split('T')[0];
        if (this.usageStats.lastReset !== today) {
          this.usageStats = {
            tokensUsed: 0,
            requestsToday: 0,
            lastReset: today
          };
          this.saveUsageStats();
        }
      }
    } catch (e) {
      console.warn('Erreur chargement stats IA:', e);
    }
  }

  /**
   * Sauvegarder les stats d'utilisation
   */
  saveUsageStats() {
    try {
      localStorage.setItem(USAGE_KEY, JSON.stringify(this.usageStats));
    } catch (e) {
      console.warn('Erreur sauvegarde stats IA:', e);
    }
  }

  /**
   * Vérifier le rate limiting
   */
  checkRateLimit() {
    const now = Date.now();
    // Nettoyer les timestamps > 1 minute
    this.requestTimestamps = this.requestTimestamps.filter(t => now - t < 60000);

    if (this.requestTimestamps.length >= MAX_REQUESTS_PER_MINUTE) {
      throw new Error('Limite de requêtes atteinte. Réessayez dans quelques secondes.');
    }

    if (this.usageStats.tokensUsed >= DAILY_TOKEN_LIMIT) {
      throw new Error('Limite quotidienne de tokens atteinte.');
    }
  }

  /**
   * Appeler l'API Gemini
   */
  async callGemini(prompt) {
    if (!this.isAvailable()) {
      throw new Error('Service IA non disponible');
    }

    this.checkRateLimit();
    this.requestTimestamps.push(Date.now());

    try {
      const response = await fetch(`${API_URL}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_NONE"
            }
          ]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const apiError = new Error(errorData.error?.message || 'Erreur API Gemini');
        captureError(apiError, 'Gemini API response', SEVERITY.API_QUOTA);
        throw apiError;
      }

      const data = await response.json();

      // Mettre à jour les stats (estimation)
      const tokensUsed = prompt.length / 4 + 500; // Estimation grossière
      this.usageStats.tokensUsed += tokensUsed;
      this.usageStats.requestsToday++;
      this.saveUsageStats();

      // Extraire le texte de la réponse
      const responseText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      return responseText;
    } catch (error) {
      captureError(error, 'Gemini API call', SEVERITY.BACKGROUND);
      throw error;
    }
  }

  /**
   * Parser une tâche depuis du langage naturel
   */
  async parseNaturalLanguageTask(input, context = {}) {
    if (!input || input.trim().length < 3) {
      return null;
    }

    // Vérifier le cache
    const cacheKey = `parse:${input.toLowerCase().trim()}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log('📦 Résultat IA depuis cache');
      return cached;
    }

    // Si offline ou API non dispo, utiliser le fallback
    if (!navigator.onLine || !this.isAvailable()) {
      console.log('📴 Mode offline, utilisation du fallback');
      return this.localFallbackParse(input, context);
    }

    try {
      const now = new Date();
      const prompt = PROMPTS.parseTask
        .replace('{input}', input)
        .replace('{currentDate}', context.date || now.toISOString().split('T')[0])
        .replace('{currentTime}', now.toTimeString().slice(0, 5));

      const response = await this.callGemini(prompt);

      // Parser le JSON de la réponse (nettoyer les backticks markdown si présents)
      let cleanResponse = response
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        console.warn('Réponse IA non parsable, fallback');
        return this.localFallbackParse(input, context);
      }

      const result = JSON.parse(jsonMatch[0]);

      // Valider et compléter
      const parsedTask = {
        task: result.task || input.trim(),
        time: result.time || null,
        duration: result.duration || 30,
        category: ['vital', 'chore', 'pleasure', 'reward'].includes(result.category)
          ? result.category
          : 'chore',
        date: result.date || context.date || now.toISOString().split('T')[0],
        instructions: Array.isArray(result.instructions) ? result.instructions : [],
        confidence: typeof result.confidence === 'number' ? result.confidence : 0.8,
        isCustom: true,
        iconName: this.getIconForCategory(result.category)
      };

      // Mettre en cache
      this.setCache(cacheKey, parsedTask);

      return parsedTask;
    } catch (error) {
      console.warn('Erreur parsing IA, fallback:', error);
      return this.localFallbackParse(input, context);
    }
  }

  /**
   * Fallback local pour le parsing (regex-based)
   */
  localFallbackParse(input, context = {}) {
    const now = new Date();
    const text = input.toLowerCase().trim();

    // Extraction de l'heure (15h, 15h30, 15:30)
    const timeMatch = text.match(/(\d{1,2})[h:](\d{2})?/);
    let time = null;
    if (timeMatch) {
      const hours = timeMatch[1].padStart(2, '0');
      const minutes = (timeMatch[2] || '00').padStart(2, '0');
      time = `${hours}:${minutes}`;
    }

    // Mots-clés pour l'heure
    if (!time) {
      if (text.includes('matin')) time = '09:00';
      else if (text.includes('midi')) time = '12:00';
      else if (text.includes('après-midi') || text.includes('aprem')) time = '14:00';
      else if (text.includes('soir')) time = '19:00';
    }

    // Extraction de la durée
    const durationMatch = text.match(/(\d+)\s*(min|minute|h|heure)/);
    let duration = 30;
    if (durationMatch) {
      duration = parseInt(durationMatch[1]);
      if (durationMatch[2].startsWith('h')) {
        duration *= 60;
      }
    }

    // Extraction de la date
    let date = context.date || now.toISOString().split('T')[0];
    if (text.includes('demain')) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      date = tomorrow.toISOString().split('T')[0];
    } else if (text.includes('après-demain') || text.includes('après demain')) {
      const afterTomorrow = new Date(now);
      afterTomorrow.setDate(afterTomorrow.getDate() + 2);
      date = afterTomorrow.toISOString().split('T')[0];
    }

    // Détection de catégorie
    let category = 'chore';
    const vitalWords = ['médicament', 'medicament', 'pilule', 'docteur', 'médecin', 'medecin', 'traitement', 'rdv', 'rendez-vous'];
    const pleasureWords = ['pause', 'café', 'cafe', 'ami', 'jeu', 'film', 'repos', 'marche', 'lecture', 'détente', 'detente'];
    const rewardWords = ['cadeau', 'achat', 'shopping', 'resto', 'restaurant', 'récompense', 'recompense'];

    if (vitalWords.some(w => text.includes(w))) {
      category = 'vital';
    } else if (pleasureWords.some(w => text.includes(w))) {
      category = 'pleasure';
    } else if (rewardWords.some(w => text.includes(w))) {
      category = 'reward';
    }

    // Nettoyer le nom de la tâche
    let taskName = input
      .replace(/\d{1,2}[h:]\d{0,2}/gi, '')
      .replace(/\d+\s*(min|minute|h|heure)s?/gi, '')
      .replace(/demain|après-demain|matin|midi|après-midi|soir/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Mettre en majuscule la première lettre
    taskName = taskName.charAt(0).toUpperCase() + taskName.slice(1);

    return {
      task: taskName || input.trim(),
      time,
      duration,
      category,
      date,
      instructions: [],
      confidence: 0.5, // Faible confiance pour le fallback
      isCustom: true,
      iconName: this.getIconForCategory(category)
    };
  }

  /**
   * Obtenir des suggestions contextuelles
   */
  async getSuggestions(context = {}) {
    const cacheKey = `suggestions:${JSON.stringify(context)}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    if (!navigator.onLine || !this.isAvailable()) {
      return this.getDefaultSuggestions(context);
    }

    try {
      const now = new Date();
      const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
      const moodLabels = ['Très bas', 'Bas', 'Neutre', 'Bien', 'Excellent'];
      const energyLabels = ['Épuisé', 'Fatigué', 'Normal', 'Énergique'];

      const prompt = PROMPTS.suggestions
        .replace('{time}', now.toTimeString().slice(0, 5))
        .replace('{dayName}', dayNames[now.getDay()])
        .replace('{mood}', context.mood || 3)
        .replace('{moodLabel}', moodLabels[(context.mood || 3) - 1])
        .replace('{energy}', context.energy || 3)
        .replace('{energyLabel}', energyLabels[(context.energy || 3) - 1])
        .replace('{completedCount}', context.completedCount || 0)
        .replace('{remainingVital}', context.remainingVital || 0)
        .replace('{pattern}', context.pattern || 'Aucun pattern détecté');

      const response = await this.callGemini(prompt);

      // Nettoyer et parser
      let cleanResponse = response
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        return this.getDefaultSuggestions(context);
      }

      const result = JSON.parse(jsonMatch[0]);
      const suggestions = result.suggestions || [];

      // Mettre en cache pour 1 heure
      this.setCache(cacheKey, suggestions, 60 * 60 * 1000);

      return suggestions;
    } catch (error) {
      console.warn('Erreur suggestions IA:', error);
      return this.getDefaultSuggestions(context);
    }
  }

  /**
   * Suggestions par défaut (fallback)
   */
  getDefaultSuggestions(context = {}) {
    const hour = new Date().getHours();
    const suggestions = [];

    // Suggestions basées sur l'heure
    if (hour >= 7 && hour < 9) {
      suggestions.push({
        text: "C'est le matin ! As-tu pris tes médicaments ?",
        type: 'reminder',
        emoji: '💊',
        priority: 5
      });
    } else if (hour >= 12 && hour < 14) {
      suggestions.push({
        text: "Pause déjeuner ! Prends le temps de manger.",
        type: 'break',
        emoji: '🍽️',
        priority: 4
      });
    } else if (hour >= 15 && hour < 17) {
      suggestions.push({
        text: "Une petite pause café ?",
        type: 'break',
        emoji: '☕',
        task: { task: 'Pause café', time: null, duration: 15, category: 'pleasure' },
        priority: 3
      });
    } else if (hour >= 21) {
      suggestions.push({
        text: "Il est tard ! Pense à ta routine du soir.",
        type: 'reminder',
        emoji: '🌙',
        priority: 4
      });
    }

    // Suggestions basées sur l'énergie
    if (context.energy && context.energy <= 2) {
      suggestions.push({
        text: "Énergie basse ? Une tâche facile ou une pause !",
        type: 'encouragement',
        emoji: '🔋',
        priority: 4
      });
    }

    // Encouragement si beaucoup de tâches complétées
    if (context.completedCount && context.completedCount >= 5) {
      suggestions.push({
        text: `Bravo ! ${context.completedCount} tâches complétées ! 🎉`,
        type: 'encouragement',
        emoji: '⭐',
        priority: 5
      });
    }

    return suggestions;
  }

  /**
   * Générer un planning optimal
   */
  async generateOptimalSchedule(tasks, constraints = {}) {
    if (!tasks || tasks.length === 0) {
      return { schedule: [], warnings: [] };
    }

    if (!navigator.onLine || !this.isAvailable()) {
      return this.localScheduleOptimization(tasks, constraints);
    }

    try {
      const prompt = PROMPTS.planDay
        .replace('{tasksJSON}', JSON.stringify(tasks.map(t => ({
          id: t.id,
          task: t.task,
          duration: t.duration,
          category: t.category
        }))))
        .replace('{availableSlots}', constraints.availableSlots || '08:00-21:00')
        .replace('{energyLevel}', constraints.energy || 3)
        .replace('{preference}', constraints.preference || 'flexible');

      const response = await this.callGemini(prompt);

      // Nettoyer et parser
      let cleanResponse = response
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        return this.localScheduleOptimization(tasks, constraints);
      }

      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      console.warn('Erreur planification IA:', error);
      return this.localScheduleOptimization(tasks, constraints);
    }
  }

  /**
   * Optimisation locale du planning (fallback)
   */
  localScheduleOptimization(tasks, constraints = {}) {
    const schedule = [];
    const warnings = [];

    // Trier par catégorie (vital en premier)
    const sortedTasks = [...tasks].sort((a, b) => {
      const priority = { vital: 0, chore: 1, pleasure: 2, reward: 3 };
      return (priority[a.category] || 2) - (priority[b.category] || 2);
    });

    let currentTime = 8 * 60; // 08:00 en minutes
    let choreCount = 0;

    sortedTasks.forEach(task => {
      // Insérer une pause après 2 corvées consécutives
      if (task.category === 'chore') {
        choreCount++;
        if (choreCount > 2) {
          currentTime += 15; // Pause 15 min
          choreCount = 0;
        }
      } else {
        choreCount = 0;
      }

      const hours = Math.floor(currentTime / 60).toString().padStart(2, '0');
      const minutes = (currentTime % 60).toString().padStart(2, '0');

      schedule.push({
        taskId: task.id,
        suggestedTime: `${hours}:${minutes}`,
        reason: task.category === 'vital' ? 'Prioritaire' : 'Créneau disponible'
      });

      currentTime += task.duration + 15; // Ajouter buffer de 15 min
    });

    // Vérifier la surcharge
    if (currentTime > 21 * 60) {
      warnings.push('Attention : la journée semble chargée. Considère reporter certaines tâches.');
    }

    return { schedule, warnings };
  }

  /**
   * Générer des sous-tâches intelligentes pour une tâche
   * Adapté TDAH/bipolaire avec étapes très détaillées
   */
  async generateSubTasks(task, context = {}) {
    if (!task || !task.task) {
      return this.getDefaultSubTasks(task);
    }

    // Cache key basé sur le nom de la tâche
    const cacheKey = `subtasks:${task.task.toLowerCase()}:${task.category}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log('📦 Sous-tâches depuis cache');
      return cached;
    }

    if (!navigator.onLine || !this.isAvailable()) {
      console.log('📴 Mode offline, sous-tâches par défaut');
      return this.getDefaultSubTasks(task);
    }

    try {
      const categoryLabels = {
        vital: 'Santé/Médicaments - PRIORITAIRE',
        chore: 'Corvée/Travail',
        pleasure: 'Plaisir/Détente',
        reward: 'Récompense'
      };

      const prompt = PROMPTS.generateSubTasks
        .replace('{taskName}', task.task)
        .replace('{category}', categoryLabels[task.category] || 'Tâche générale')
        .replace('{duration}', task.duration || 30)
        .replace('{context}', context.mood
          ? `Humeur: ${context.mood}/5, Énergie: ${context.energy}/4`
          : 'Contexte normal');

      const response = await this.callGemini(prompt);

      // Parser le JSON
      let cleanResponse = response
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        return this.getDefaultSubTasks(task);
      }

      const result = JSON.parse(jsonMatch[0]);

      // Valider et enrichir
      const subTasksData = {
        subTasks: (result.subTasks || []).map((st, idx) => ({
          id: `${task.id || 'task'}-step-${idx + 1}`,
          step: st.step || idx + 1,
          title: st.title || `Étape ${idx + 1}`,
          description: st.description || '',
          duration: Math.min(st.duration || 5, 10), // Max 10 min par étape
          tip: st.tip || '',
          isCheckpoint: st.isCheckpoint || false,
          suggestion: st.suggestion || null,
          completed: false
        })),
        totalDuration: result.totalDuration || task.duration || 30,
        difficultyLevel: result.difficultyLevel || 'moyen',
        encouragement: result.encouragement || 'Tu peux le faire ! Une étape à la fois. 💪',
        breakReminder: result.breakReminder || null,
        mainSuggestion: result.mainSuggestion || null
      };

      // Mettre en cache pour 12 heures
      this.setCache(cacheKey, subTasksData, 12 * 60 * 60 * 1000);

      return subTasksData;
    } catch (error) {
      console.warn('Erreur génération sous-tâches:', error);
      return this.getDefaultSubTasks(task);
    }
  }

  /**
   * Sous-tâches par défaut (fallback intelligent)
   */
  getDefaultSubTasks(task) {
    const taskName = task?.task || 'la tâche';
    const taskLower = taskName.toLowerCase();
    const category = task?.category || 'chore';
    const duration = task?.duration || 30;

    // Détection du type de tâche pour suggestion principale uniquement
    const taskType = this.detectTaskType(taskLower);

    // Templates de sous-tâches simplifiés (sans suggestions inline)
    const templates = {
      vital: [
        { title: '🧘 Respire', description: 'Prends 3 grandes respirations', duration: 1, tip: '💡 Ça prépare ton esprit', isCheckpoint: false },
        { title: '📍 Déplace-toi', description: 'Va vers l\'endroit où tu dois agir', duration: 1, tip: '💡 Le plus dur c\'est de bouger !', isCheckpoint: false },
        { title: `✅ ${taskName}`, description: 'Fais-le maintenant', duration: Math.max(duration - 5, 3), tip: '💡 Une seule action à la fois', isCheckpoint: true },
        { title: '🎉 Bravo !', description: 'Tu l\'as fait !', duration: 1, tip: '💡 Chaque victoire compte', isCheckpoint: true }
      ],
      chore: [
        { title: '🎯 Visualise', description: 'Imagine le résultat final', duration: 1, tip: '💡 La motivation vient après le démarrage', isCheckpoint: false },
        { title: '👟 Prépare', description: 'Rassemble ce qu\'il te faut', duration: 2, tip: '💡 Tout sous la main = moins de distractions', isCheckpoint: false },
        { title: `⚡ ${taskName}`, description: 'Juste 5 minutes pour commencer', duration: 5, tip: '💡 Tu peux arrêter après si tu veux', isCheckpoint: true },
        { title: '🔄 Continue', description: 'Tu es lancé(e) !', duration: Math.max(duration - 12, 5), tip: '💡 Profite du flow', isCheckpoint: false },
        { title: '✨ Termine', description: 'Finalise proprement', duration: 2, tip: '💡 Un bon finish !', isCheckpoint: true }
      ],
      pleasure: [
        { title: '😊 Autorise-toi', description: 'Tu MÉRITES ce moment', duration: 1, tip: '💡 Pas de culpabilité', isCheckpoint: false },
        { title: `🎈 ${taskName}`, description: 'Vis pleinement ce moment', duration: duration - 2, tip: '💡 Sois présent(e) à 100%', isCheckpoint: true },
        { title: '🙏 Savoure', description: 'Prends soin de toi', duration: 1, tip: '💡 Le self-care est productif', isCheckpoint: true }
      ],
      reward: [
        { title: '🏆 Mérité !', description: 'Cette récompense vient après tes efforts', duration: 1, tip: '💡 Savoure ce moment', isCheckpoint: false },
        { title: `🎁 ${taskName}`, description: 'Profite pleinement', duration: duration - 2, tip: '💡 Tu as travaillé pour ça', isCheckpoint: true }
      ]
    };

    const subTasks = (templates[category] || templates.chore).map((st, idx) => ({
      id: `${task?.id || 'task'}-step-${idx + 1}`,
      step: idx + 1,
      ...st,
      completed: false
    }));

    return {
      subTasks,
      totalDuration: subTasks.reduce((sum, st) => sum + st.duration, 0),
      difficultyLevel: category === 'vital' ? 'important' : 'moyen',
      encouragement: this.getEncouragementMessage(category),
      breakReminder: duration > 45 ? '⏰ Pause recommandée après 25 min' : null,
      mainSuggestion: this.getMainSuggestion(taskType)
    };
  }

  /**
   * Détecter le type de tâche pour suggestions contextuelles
   */
  detectTaskType(taskLower) {
    const patterns = {
      cooking: ['cuisine', 'cuisinier', 'repas', 'déjeuner', 'dîner', 'petit-déjeuner', 'manger', 'préparer à manger', 'recette', 'plat'],
      walking: ['marche', 'balade', 'promenade', 'sortir', 'sortie', 'parc', 'dehors', 'air frais', 'balader'],
      cleaning: ['ménage', 'nettoyer', 'ranger', 'vaisselle', 'lessive', 'aspirateur', 'poussière', 'nettoyage'],
      shopping: ['courses', 'acheter', 'supermarché', 'magasin', 'shopping', 'commissions'],
      medication: ['médicament', 'pilule', 'traitement', 'médoc', 'comprimé', 'gélule'],
      exercise: ['sport', 'exercice', 'gym', 'yoga', 'étirement', 'musculation', 'fitness'],
      relaxation: ['détente', 'repos', 'relaxation', 'méditation', 'calme', 'pause'],
      work: ['travail', 'bureau', 'email', 'réunion', 'projet', 'tâche pro']
    };

    for (const [type, keywords] of Object.entries(patterns)) {
      if (keywords.some(kw => taskLower.includes(kw))) {
        return type;
      }
    }
    return 'general';
  }

  /**
   * Obtenir la suggestion principale pour une tâche
   */
  getMainSuggestion(taskType) {
    const mainSuggestions = {
      cooking: {
        type: 'recipe',
        title: '👨‍🍳 Recettes rapides recommandées',
        items: [
          { name: 'Pâtes carbonara (15 min)', url: 'https://www.marmiton.org/recettes/recette_pates-a-la-carbonara_20632.aspx' },
          { name: 'Omelette fromage (10 min)', url: 'https://www.marmiton.org/recettes/recette_omelette-au-fromage_19640.aspx' },
          { name: 'Croque-monsieur (5 min)', url: 'https://www.marmiton.org/recettes/recette_croque-monsieur_11418.aspx' },
          { name: 'Salade César (15 min)', url: 'https://www.marmiton.org/recettes/recette_salade-cesar_34835.aspx' }
        ]
      },
      walking: {
        type: 'place',
        title: '🌳 Espaces verts près de chez toi (75019)',
        items: [
          { name: 'Parc des Buttes-Chaumont (5 min)', url: 'https://www.google.com/maps/dir/46+bis+Avenue+Jean+Jaur%C3%A8s,+75019+Paris/Parc+des+Buttes+Chaumont' },
          { name: 'Parc de la Villette (15 min)', url: 'https://www.google.com/maps/dir/46+bis+Avenue+Jean+Jaur%C3%A8s,+75019+Paris/Parc+de+la+Villette' },
          { name: 'Canal de l\'Ourcq (10 min)', url: 'https://www.google.com/maps/dir/46+bis+Avenue+Jean+Jaur%C3%A8s,+75019+Paris/Canal+de+l%27Ourcq' },
          { name: 'Bassin de la Villette (10 min)', url: 'https://www.google.com/maps/dir/46+bis+Avenue+Jean+Jaur%C3%A8s,+75019+Paris/Bassin+de+la+Villette' }
        ]
      },
      shopping: {
        type: 'shopping',
        title: '🛒 Magasins proches',
        items: [
          { name: 'Carrefour City (2 min)', url: 'https://www.google.com/maps/search/carrefour+city+avenue+jean+jaures+75019' },
          { name: 'Franprix (5 min)', url: 'https://www.google.com/maps/search/franprix+75019+jean+jaures' },
          { name: 'Lidl Stalingrad (10 min)', url: 'https://www.google.com/maps/search/lidl+stalingrad+paris' }
        ]
      },
      exercise: {
        type: 'place',
        title: '🏃 Lieux pour faire du sport',
        items: [
          { name: 'Canal de l\'Ourcq - Running', url: 'https://www.google.com/maps/place/Canal+de+l%27Ourcq' },
          { name: 'Parc des Buttes-Chaumont - Jogging', url: 'https://www.google.com/maps/place/Parc+des+Buttes+Chaumont' },
          { name: 'Basic-Fit Jaurès (salle)', url: 'https://www.google.com/maps/search/basic+fit+jaures+paris' }
        ]
      }
    };

    return mainSuggestions[taskType] || null;
  }

  /**
   * Message d'encouragement par catégorie
   */
  getEncouragementMessage(category) {
    const messages = {
      vital: '💊 Ta santé est prioritaire. Une étape à la fois, tu vas y arriver !',
      chore: '💪 Cette corvée te semblera plus facile une fois commencée. Fais juste le premier pas !',
      pleasure: '😊 Tu mérites ce moment de détente. Profites-en pleinement !',
      reward: '🌟 Bravo pour tes efforts ! Cette récompense est bien méritée !'
    };
    return messages[category] || 'Tu es capable de grandes choses. Commence par la première étape ! 💪';
  }

  /**
   * Obtenir l'icône pour une catégorie
   */
  getIconForCategory(category) {
    const icons = {
      vital: 'AlertCircle',
      chore: 'Briefcase',
      pleasure: 'Coffee',
      reward: 'Trophy'
    };
    return icons[category] || 'ListTodo';
  }

  /**
   * Obtenir les stats d'utilisation
   */
  getUsageStats() {
    return {
      ...this.usageStats,
      remainingTokens: DAILY_TOKEN_LIMIT - this.usageStats.tokensUsed,
      percentUsed: Math.round((this.usageStats.tokensUsed / DAILY_TOKEN_LIMIT) * 100)
    };
  }
}

// Singleton
const aiService = new AIService();
export default aiService;
